// Access administration — the list of carriers (design system 06-modules §2). Each group shows its
// name, its member count and what it confers, in place, so the screen is an administration view
// rather than a directory. Superuser-only (permission-model P11): the route is gated by /api/me and
// the server refuses everyone else with a 403 this page renders as permission-denied.
import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { AdminGroupRow, AdminGroupsResponse } from '../../shared/api-types.js';
import type { AdminSection } from '../../shared/enums.js';
import type { ApiFailure } from '../api.js';
import { PATHS } from '../paths.js';
import { AdminSectionStrip } from '../shell/admin.js';
import {
  Button,
  Chip,
  DataTable,
  Dialog,
  FormErrorSummary,
  LinkButton,
  Page,
  PageHead,
  RelativeTime,
  StatusPill,
  TextField,
  TextareaField,
  formatCount,
  pluralise,
  useIsMobile,
  useRequest,
  useToast,
  type DataTableColumn,
  type DataTableStatus,
} from '../ui/index.js';
import { adminApi } from './admin-api.js';
import { describeConfers, groupSortValue, type GroupSortKey } from './admin-helpers.js';
import { OperationFailure } from './parts.js';
import { SuperuserHistory } from './superuser-history.js';
import './admin.css';

export default function AdminGroupsPage() {
  const groups = useRequest<AdminGroupsResponse>((signal) => adminApi.groups(signal), []);
  const [creating, setCreating] = useState(false);
  const rows = groups.data?.items ?? [];

  // Folding (04-data §1.7; ADR 0168): Updated (scanning only) first, then Warnings. The group's name, its member count
  // and what it confers are the list 06-modules §2.2 requires in place — the module's answer — and never fold, nor do
  // the actions.
  const columns = useMemo<DataTableColumn<AdminGroupRow>[]>(() => {
    const sortBy = (key: GroupSortKey) => (row: AdminGroupRow) => groupSortValue(row, key);
    return [
      {
        key: 'name',
        header: 'Group',
        card: 'title',
        sortValue: sortBy('name'),
        cell: (row) => (
          <span className="stack stack--1">
            <Link to={PATHS.adminGroup(row.id)} className="hit-area">{row.name}</Link>
            {row.description ? <span className="text-xs muted">{row.description}</span> : null}
          </span>
        ),
      },
      { key: 'members', header: 'Members', type: 'number', numeric: true, sortValue: sortBy('members'), cell: (row) => formatCount(row.memberCount) },
      {
        key: 'confers',
        header: 'Confers',
        type: 'number',
        sortValue: sortBy('confers'),
        // The server's word on `act` (actDelegableSections) decides "view and act"; with no response yet, "view" alone.
        cell: (row) => <ConfersChips row={row} actDelegable={groups.data?.actDelegableSections ?? []} />,
      },
      {
        key: 'warnings',
        header: 'Warnings',
        type: 'number',
        fold: 2,
        sortValue: sortBy('warnings'),
        cell: (row) => (row.warnings.length > 0
          ? <StatusPill tone="warning" title={row.warnings.map((warning) => warning.message).join(' ')}>{pluralise(row.warnings.length, { one: '# warning', other: '# warnings' })}</StatusPill>
          : <span className="muted">—</span>),
      },
      { key: 'updated', header: 'Updated', type: 'date', fold: 1, sortValue: sortBy('updated'), cell: (row) => <RelativeTime value={row.updatedAt} /> },
      {
        key: 'actions',
        header: <span className="visually-hidden">Actions</span>,
        name: 'Actions',
        sortable: false,
        align: 'end',
        card: 'actions',
        cell: (row) => <LinkButton size="sm" variant="secondary" to={PATHS.adminGroup(row.id)}>Open</LinkButton>,
      },
    ];
  }, [groups.data?.actDelegableSections]);

  const status: DataTableStatus = groups.status === 'loading'
    ? { kind: 'loading', rowCount: 4 }
    : groups.status === 'error' && groups.error
      ? { kind: 'error', failure: groups.error, onRetry: groups.reload }
      : { kind: 'ready' };

  // Why: the page's one primary action. On a phone it pins above the tab bar, at the end of the list, instead of sitting
  // in the stacked head (02-patterns §2.1: exactly one primary action on a long list SHOULD; owner's pick C7, conformity
  // 2026-10-05); on desktop it stays in the head. Rendered once, where it shows, so the tab order follows the visual
  // order (01-foundations §7.2) — never a hidden twin.
  const isMobile = useIsMobile();
  const newGroup = <Button variant="primary" icon="plus" block={isMobile} onClick={() => setCreating(true)}>New group</Button>;

  return (
    <Page measure="wide">
      <PageHead title="Groups" strip={<AdminSectionStrip />} actions={isMobile ? undefined : newGroup} />
      <DataTable<AdminGroupRow, string>
        caption={groups.data ? `Groups (${formatCount(groups.data.total)})` : 'Groups'}
        failureNoun="groups"
        captionVisible
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        status={status}
        sort={{ defaultSort: { key: 'name', dir: 'asc' } }}
        clientSort
        empty={{
          title: 'No groups yet',
          description: 'Rights come only from groups — create one to give people access.',
          // Why secondary (03-components §2 "one primary per view, and only one"; review of 2026-10-05, finding 15): the
          // page's one primary is `newGroup` above — in the head, or pinned above the tab bar on a phone, where this
          // empty state sat directly beneath it as a second primary. As To-dos words its empty state's offer.
          action: <Button variant="secondary" icon="plus" onClick={() => setCreating(true)}>New group</Button>,
        }}
      />
      {isMobile ? <div className="page-pinned-action">{newGroup}</div> : null}
      {/* Why here, after the list and its pinned action (permission model 05-administration §3 "Reaches the superuser"):
          the superuser is no group, so its grants have no carrier page of their own; they sit beside the carriers whose
          histories they join, on the page an access review starts from. The pinned "New group" stays the list's end. */}
      <SuperuserHistory />
      <CreateGroupDialog open={creating} onClose={() => setCreating(false)} />
    </Page>
  );
}

function ConfersChips({ row, actDelegable }: { row: AdminGroupRow; actDelegable: readonly AdminSection[] }) {
  const phrases = describeConfers(row.confers, actDelegable);
  if (phrases.length === 0) return <span className="muted">Nothing yet</span>;
  return (
    <ul className="confers-chips" role="list" aria-label={`What ${row.name} confers`}>
      {phrases.map((phrase) => (
        <li key={phrase}><Chip>{phrase}</Chip></li>
      ))}
    </ul>
  );
}

/** Creates a group with its details only; grants and members are edited on the group's page. */
function CreateGroupDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [policyNote, setPolicyNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const dirty = name.trim() !== '' || description.trim() !== '' || policyNote.trim() !== '';

  const reset = (): void => {
    setName('');
    setDescription('');
    setPolicyNote('');
    setFailure(null);
    setNameError(null);
  };

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (name.trim() === '') {
      setNameError('The group needs a name.');
      return;
    }
    setBusy(true);
    setFailure(null);
    setNameError(null);
    const result = await adminApi.createGroup({
      name: name.trim(),
      description: description.trim() === '' ? null : description.trim(),
      policyNote: policyNote.trim() === '' ? null : policyNote.trim(),
    });
    setBusy(false);
    if (!result.ok) {
      if (result.code === 'conflict' || result.fields?.includes('name')) setNameError(result.message);
      else setFailure(result);
      return;
    }
    toast.success(`Created ${result.data.name}.`);
    reset();
    onClose();
    navigate(PATHS.adminGroup(result.data.id));
  };

  return (
    <Dialog
      open={open}
      onClose={() => { if (!busy) { reset(); onClose(); } }}
      title="New group"
      description="Grants and members come next, on the group's page."
      initialFocus="first"
      unsavedChanges={dirty}
      actions={
        <>
          <Button variant="secondary" onClick={() => { reset(); onClose(); }} disabled={busy}>Cancel</Button>
          <Button variant="primary" type="submit" form="create-group-form" loading={busy}>Create group</Button>
        </>
      }
    >
      <form id="create-group-form" className="stack" onSubmit={(event) => void submit(event)} noValidate>
        {nameError && !failure ? <FormErrorSummary errors={[nameError]} /> : null}
        <TextField label="Name" required value={name} onChange={(event) => setName(event.target.value)} error={nameError} autoComplete="off" maxLength={120} hint="Unique; shown on every member's row." />
        <TextareaField label="Description" value={description} onChange={(event) => setDescription(event.target.value)} rows={2} maxLength={600} hint="Optional. Who this group is for." />
        <TextareaField label="Policy note" value={policyNote} onChange={(event) => setPolicyNote(event.target.value)} rows={2} maxLength={600} hint="Optional. Guidance for administrators; it enforces nothing." />
        <OperationFailure failure={failure} attempted="create the group" onDismiss={() => setFailure(null)} />
      </form>
    </Dialog>
  );
}
